import * as React from 'react';
import styled from 'styled-components';
import { forIE, forTablet } from '@westwing/ui-kit/breakPoints';
import LooksTileUiKit from '@westwing/ui-kit/LooksTile';
import TextBox from '@westwing/ui-kit/TextBox';
import { useLooksTracking } from '../hooks/useLooksTracking';
import HybridLink from 'Client/components/Generic/Links/HybridLink';
import { LazyHybridImage } from 'Client/components/Generic/HybridImage';
import { textLinesLimitClipCss } from 'Client/components/Generic/Text/StyledText';
import { LookPromotion } from 'Client/redux/looksPromotionalElements/types';
import { Maybe } from 'Common/types/utility';
import useInView from 'Client/hooks/dom/useInView';
import { TrackLLPImpressionEvents, TrackLooksActions } from 'Common/ga/types';

const StyledPromotionalElement = styled(LooksTileUiKit)<{ color?: string; onClick?: () => void }>`
    margin-bottom: 20px;
    ${forTablet`
        margin-bottom: 0px;
    `}

    &::before {
        content: '';
        position: absolute;
        height: calc(100% - 10px);
        width: 100%;
        z-index: -1;
        margin-left: 30px;
        background-color: ${({ color }) => color};
        ${forTablet`
            margin-left: 0px;
        `}
    }

    ${forIE`
        flex-basis: calc( (100% - 20px) / 4 );
    `}
`;

const StyledHybridLink = styled(HybridLink)`
    position: relative;
    background-color: transparent;
`;

const StyledHybridLinkText = styled(HybridLink)`
    color: white;
    padding-left: 10px;
`;

const LazyStyledImage = styled(LazyHybridImage)`
    position: absolute;
    height: 100%;

    picture {
        position: absolute;
        padding: 10px 10px 0;
        overflow: hidden;
        img {
            ${forIE`
                height: 330px;
                width: 280px;
            `}
        }
    }
`;

const Title = styled(TextBox.Header)`
    ${textLinesLimitClipCss(1)}
    ${forIE`
        display: block;
    `}
`;

const PromotionalElement = ({ title, category, link, cta, image, color }: LookPromotion) => {
    const { LLP: LLPTracking } = useLooksTracking();

    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    useInView<HTMLDivElement>({
        containerRef,
        options: { threshold: 0.8 },
        onVisibleCallback: () =>
            LLPTracking.trackImpression(
                TrackLLPImpressionEvents.LookPromotionImpression,
                TrackLooksActions.LLPPromotionalElementImpression,
                title
            ),
    });

    const onClick = () => LLPTracking.track(TrackLooksActions.LLPPromotionalElementClick, title);

    return (
        <StyledPromotionalElement
            data-testid="promotional-element"
            color={color}
            onClick={onClick}
            forwaredRef={containerRef}
        >
            <LooksTileUiKit.ImageWrapper as={StyledHybridLink} to={link} isPortrait>
                {image && <LazyStyledImage alt={title} fallbackSrc={image} desktopSrc={image} mobileSrc={image} />}
            </LooksTileUiKit.ImageWrapper>
            <TextBox>
                <TextBox.Prefix as={StyledHybridLinkText} to={link}>
                    {category}
                </TextBox.Prefix>
                <Title as={StyledHybridLinkText} to={link}>
                    {title}
                </Title>
                <TextBox.Cta as={StyledHybridLinkText} to={link}>
                    {cta}
                </TextBox.Cta>
            </TextBox>
        </StyledPromotionalElement>
    );
};

export default PromotionalElement;
